import React, { useEffect, useState, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Grid, makeStyles, Typography, Button, TextField, MenuItem, Checkbox } from '@material-ui/core'
import { useTranslation } from 'react-i18next'
import PricingConditionList from './pricingConditionList/PricingConditionList'
import PricingConditionCard from './pricingConditionCard/PricingConditionCard'
import { useCalcHeightToBottom } from '../../utils/ui'
import PricingConditionAddEditDialog from './pricingConditionAddEditDialog/PricingConditionAddEditDialog'
import { DeleteConfirmationDialog } from '../../shared/components/deleteConfirmationDialog'
import { PricingConditionModel } from '../../shared/types/pricingCondition'
import { deletePricingCondition, fetchPricingConditions } from '../../redux/actions/pricingConditions'
import { fetchTariffs } from '../../redux/actions/tariffs'
import { fetchTags } from '../../redux/actions/tags'
import { fetchTenants } from '../../redux/actions/dashboard'
import { TagModel, TagTypes } from '../../shared/types/tag'
import { TariffModel } from '../../shared/types/tariff'
import { matchesTenantFilter, useTenantFilter } from '../../utils/tenantFilter'
import { getPricingConditionValidity, PRICING_CONDITION_VALIDITIES } from './pricingConditionValidity'

const useStyles = makeStyles((theme) => ({
    headerWrapper: {
        padding: theme.spacing(1),
    },
    actionButtonWrapper: {
        padding: theme.spacing(1, 0),
        textAlign: 'right',
    },
    listWrapper: {
        paddingTop: theme.spacing(2),
    },
    cardWrapper: {
        paddingTop: theme.spacing(2),
        width: '100%',
    },
    filterWrapper: {
        padding: theme.spacing(1),
        paddingTop: theme.spacing(2),
        paddingRight: 0,
        display: 'flex',
        gap: theme.spacing(2),
    },
    input: {
        backgroundColor: '#f5f5f5',
        borderRadius: theme.shape.borderRadius,
        overflow: 'hidden',
        border: '1px solid #ddd',

        '& .MuiFilledInput-root': {
            backgroundColor: '#f5f5f5',
            borderRadius: theme.shape.borderRadius,
            paddingBottom: '6px',
            height: '40px',
            boxShadow: 'none',
            '&:before, &:after': {
                display: 'none',
            },
        },
        '& .MuiInputLabel-root': {
            top: '-7px',
            fontSize: '0.9rem',
        },
        '& .MuiSelect-icon': {
            top: 'calc(50% - 12px)',
        },
    },
    checkbox: {
        color: '#6100ED',
        '&$checked': {
            color: '#6100ED',
        },
        '&$disabled': {
            color: '#A0A0A0',
        },
        '&$checked$disabled': {
            color: '#A0A0A0',
        },
        marginBottom: '4px',
        padding: '0 8px 0 0',
    },
    checked: {},
    disabled: {},
}))

export default function PricingConditions() {
    const classes = useStyles()
    const { t } = useTranslation()
    const dispatch = useDispatch()

    const tenantId = useSelector((state: any) => state.login.value.user.tenantId)
    const [selectedPricingCondition, setSelectedPricingCondition] = useState<PricingConditionModel>()

    // Lokálny stav pre textové vyhľadávanie
    const [search, setSearch] = useState('')

    const [addEditDialogState, setAddEditDialogState] = useState({
        open: false,
        isAdd: true,
        onClose: (pricingCondition: PricingConditionModel) => { },
        initialModel: undefined as PricingConditionModel | undefined,
    })

    const [addedPricingCondition, setAddedPricingCondition] = useState<PricingConditionModel>()
    const [editedPricingCondition, setEditedPricingCondition] = useState<PricingConditionModel>()
    const [pricingConditionToDelete, setPricingConditionToDelete] = useState<PricingConditionModel>()

    const tenantFilter = useTenantFilter()
    const [validityFilter, setValidityFilter] = useState<string[]>(['active'])
    const [chargerTagFilter, setChargerTagFilter] = useState('')
    const [locationTagFilter, setLocationTagFilter] = useState('')
    const [userTagFilter, setUserTagFilter] = useState('')
    const [scheduleFilter, setScheduleFilter] = useState('')
    const [flexiChargingSpecificFilter, setflexiChargingSpecificFilter] = useState(false);

    const tags = useSelector((state: any) => state.tags.items.all.value)
    const tariffs = useSelector((state: any) => state.tariffs.items.value)
    const tenantTags = (tags || []).filter((tag: TagModel) => matchesTenantFilter(tag.tenantId, tenantFilter))
    const tenantTariffs = (tariffs || []).filter((tariff: TariffModel) => matchesTenantFilter(tariff.tenantId, tenantFilter))

    const userTags = tenantTags.filter((tag: TagModel) => tag.type === TagTypes[0].code)
    const chargerTags = tenantTags.filter((tag: TagModel) => tag.type === TagTypes[1].code)
    const locationTags = tenantTags.filter((tag: TagModel) => tag.type === TagTypes[2].code)

    const pricingConditions = useSelector((state: any) => state.pricingConditions.items.value) ?? []
    const pricingConditionsState = useSelector((state: any) => state.pricingConditions.items)
    const deletedPricingCondition = useSelector((state: any) => state.pricingConditions.delete.value)
    const pricingConditionDeleteExecuted = useSelector((state: any) => state.pricingConditions.delete.executed)

    const pricingConditionCardHeightFill = useCalcHeightToBottom('pricingConditionsCardWrapper')
    const handleFlexiFilterChange = (event: any) => {
        setflexiChargingSpecificFilter(event.target.checked);
    };

    const openAddDialog = () => {
        setAddEditDialogState({
            open: true,
            isAdd: true,
            onClose: onAddDialogClose,
            initialModel: undefined,
        })
    }

    const onAddDialogClose = (pricingCondition?: PricingConditionModel) => {
        setAddEditDialogState({ ...addEditDialogState, open: false })
        if (pricingCondition) {
            setAddedPricingCondition(pricingCondition)
        }
    }

    const onEditDialogClose = (pricingCondition?: PricingConditionModel) => {
        setAddEditDialogState({ ...addEditDialogState, open: false })
        if (pricingCondition) {
            setEditedPricingCondition(pricingCondition)
        }
    }

    const onPricingConditionSelected = (pricingCondition: PricingConditionModel) => {
        setSelectedPricingCondition(pricingCondition)
    }

    const onPricingConditionDelete = (pricingCondition: PricingConditionModel) => {
        setPricingConditionToDelete(pricingCondition)
    }

    const onPricingConditionEdit = (pricingCondition: PricingConditionModel) => {
        setAddEditDialogState({
            open: true,
            isAdd: false,
            onClose: onEditDialogClose,
            initialModel: pricingCondition,
        })
    }
    const handleRemovePricingCondition = async () => {
        const pricingConditionToDeleteDefined = pricingConditionToDelete as PricingConditionModel
        await dispatch(deletePricingCondition(pricingConditionToDeleteDefined))
        setPricingConditionToDelete(undefined)
    }

    useEffect(() => {
        dispatch(fetchPricingConditions())
    }, [dispatch, addedPricingCondition, editedPricingCondition, deletedPricingCondition])

    useEffect(() => {
        dispatch(fetchTariffs(tenantId))
        dispatch(fetchTags())
        dispatch(fetchTenants())
    }, [dispatch, tenantId])

    // Filtrovanie prebieha na jednom mieste vrátane textového search-u
    const filteredPricingConditions = useMemo(() => {
        const searchLower = search.toLowerCase().trim()

        return pricingConditions.filter((condition: PricingConditionModel) => {
            const matchesSearch = !searchLower || condition.name.toLowerCase().includes(searchLower)
            const matchesTenant = matchesTenantFilter(condition.tenantId, tenantFilter)
            const matchesValidity = validityFilter.length === 0 || validityFilter.includes(getPricingConditionValidity(condition))
            const matchesCharger = !chargerTagFilter || condition.chargerTag === chargerTagFilter
            const matchesLocation = !locationTagFilter || condition.locationTag === locationTagFilter
            const matchesUser = !userTagFilter || condition.userTag === userTagFilter
            const matchesSchedule = !scheduleFilter || condition.scheduleTariff === scheduleFilter
            const matchesFlexi = !flexiChargingSpecificFilter || condition.flexiChargingSpecific === true

            return (
                matchesSearch &&
                matchesTenant &&
                matchesValidity &&
                matchesCharger &&
                matchesLocation &&
                matchesUser &&
                matchesSchedule &&
                matchesFlexi
            )
        })
    }, [pricingConditions, search, tenantFilter, validityFilter, chargerTagFilter, locationTagFilter, userTagFilter, scheduleFilter, flexiChargingSpecificFilter])

    // Automatický výber prvého záznamu po zmene filtrov
    useEffect(() => {
        if (pricingConditionsState.executed) {
            if (filteredPricingConditions.length > 0) {
                if (selectedPricingCondition != null) {
                    const pricingConditionToSelect = filteredPricingConditions.find(
                        (x: any) => x.id === selectedPricingCondition.id
                    )
                    if (pricingConditionToSelect != null) {
                        setSelectedPricingCondition(pricingConditionToSelect)
                    } else {
                        setSelectedPricingCondition(filteredPricingConditions[0])
                    }
                } else {
                    setSelectedPricingCondition(filteredPricingConditions[0])
                }
            } else {
                setSelectedPricingCondition(undefined)
            }
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filteredPricingConditions, pricingConditionsState.executed])

    return (
        <>
            <Grid container spacing={0}>
                <Grid item sm={2} className={classes.headerWrapper}>
                    <Typography align="left" variant="h3">
                        {t('pricingConditions.title')}
                    </Typography>
                </Grid>
                <Grid item sm={10} className={classes.actionButtonWrapper}>
                    <Button
                        onClick={() => {
                            openAddDialog()
                        }}
                        variant="contained"
                        color="primary"
                    >
                        {t('pricingConditions.newPricingConditions')}
                    </Button>
                    {addEditDialogState.open && <PricingConditionAddEditDialog {...addEditDialogState} />}
                </Grid>
                <Grid item xs={12} className={classes.filterWrapper}>
                    {/* platnost - vyber viacerych hodnot; prazdny vyber = vsetky */}
                    <Grid item xs={2}>
                        <TextField
                            select
                            fullWidth
                            variant="filled"
                            className={classes.input}
                            label={t('pricingConditions.validity')}
                            value={validityFilter}
                            onChange={(e) => setValidityFilter(e.target.value as unknown as string[])}
                            SelectProps={{
                                multiple: true,
                                renderValue: (selected: any) =>
                                    (selected as string[]).length === 0
                                        ? t('All')
                                        : (selected as string[]).map((v) => t(`pricingConditions.validity_${v}`)).join(', '),
                            }}
                        >
                            {PRICING_CONDITION_VALIDITIES.map((v) => (
                                <MenuItem key={v} value={v}>
                                    <Checkbox
                                        checked={validityFilter.includes(v)}
                                        style={{ color: '#6100ED', padding: '4px' }}
                                    />
                                    {t(`pricingConditions.validity_${v}`)}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Grid>
                    <Grid item xs={2}>
                        <TextField
                            select
                            fullWidth
                            variant="filled"
                            className={classes.input}
                            label={t('chargerTag')}
                            value={chargerTagFilter}
                            onChange={(e) => setChargerTagFilter(e.target.value)}
                        >
                            <MenuItem value="">{t('All')}</MenuItem>
                            {(chargerTags || []).map((tag: TagModel) => (
                                <MenuItem key={tag.id} value={tag.id}>
                                    {tag.name}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Grid>
                    <Grid item xs={2}>
                        <TextField
                            select
                            fullWidth
                            variant="filled"
                            className={classes.input}
                            label={t('locationTag')}
                            value={locationTagFilter}
                            onChange={(e) => setLocationTagFilter(e.target.value)}
                        >
                            <MenuItem value="">{t('All')}</MenuItem>
                            {(locationTags || []).map((tag: TagModel) => (
                                <MenuItem key={tag.id} value={tag.id}>
                                    {tag.name}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Grid>
                    <Grid item xs={2}>
                        <TextField
                            select
                            fullWidth
                            variant="filled"
                            className={classes.input}
                            label={t('userTag')}
                            value={userTagFilter}
                            onChange={(e) => setUserTagFilter(e.target.value)}
                        >
                            <MenuItem value="">{t('All')}</MenuItem>
                            {(userTags || []).map((tag: TagModel) => (
                                <MenuItem key={tag.id} value={tag.id}>
                                    {tag.name}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Grid>
                    <Grid item xs={2}>
                        <TextField
                            select
                            fullWidth
                            variant="filled"
                            className={classes.input}
                            label={t('schedule')}
                            value={scheduleFilter}
                            onChange={(e) => setScheduleFilter(e.target.value)}
                        >
                            <MenuItem value="">{t('All')}</MenuItem>
                            {tenantTariffs.map((tariff: TariffModel) => (
                                <MenuItem key={tariff.id} value={tariff.id}>
                                    {tariff.name}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Grid>
                    <Grid item xs={2} style={{ display: 'flex', alignItems: 'center' }}>
                        <Typography variant="caption" style={{ fontSize: '14px', color: '#000000', display: 'flex', alignItems: 'center' }}>
                            <Checkbox
                                name="flexiChargingSpecificFilter"
                                checked={flexiChargingSpecificFilter}
                                onChange={handleFlexiFilterChange}
                                classes={{
                                    root: classes.checkbox,
                                    checked: classes.checked,
                                    disabled: classes.disabled,
                                }}
                            />
                            {t('locations.flexiCharging')}
                        </Typography>
                    </Grid>
                </Grid>
                <Grid item sm={4} className={classes.listWrapper}>
                    <PricingConditionList
                        selectedPricingCondition={selectedPricingCondition}
                        pricingConditions={filteredPricingConditions}
                        search={search}
                        onSearchChange={setSearch}
                        onPricingConditionSelected={onPricingConditionSelected}
                    />
                </Grid>

                <Grid item sm={8} className={classes.cardWrapper}>
                    <div id="pricingConditionCardWrapper" ref={pricingConditionCardHeightFill}>
                        {selectedPricingCondition && (
                            <PricingConditionCard
                                pricingCondition={selectedPricingCondition}
                                onDelete={() => onPricingConditionDelete(selectedPricingCondition)}
                                onEdit={() => onPricingConditionEdit(selectedPricingCondition)}
                            />
                        )}
                    </div>
                </Grid>
            </Grid>
            <DeleteConfirmationDialog
                open={Boolean(pricingConditionToDelete)}
                onConfirm={async () => {
                    handleRemovePricingCondition()
                }}
                content={t('pricingConditions.deleteContent')}
                onCancel={() => setPricingConditionToDelete(undefined)}
                inProgress={!pricingConditionDeleteExecuted}
            />
        </>
    )
}